// Project Overview / Dashboard section — the customer's "Home" page.
//
// Like-for-like port of the source-of-truth dashboard at:
//   /var/www/kitchenos/customer-standalone.html:582-731  (markup)
//   /var/www/kitchenos/customer-live.js:190-263          (renderers)
//
// The dashboard is composed of six modular cards:
//   1. Hero — status-driven gradient banner with greeting + 3 quick actions.
//   2. Stat grid — 4 tiles (New messages, Next appointment, Latest quote,
//      Estimated delivery), each navigating to its source page.
//   3. Quick action tiles — 4 tiles (View Designs, Review Quote, Make Payment,
//      Documents).
//   4. Recent activity feed — last few messages/bookings/quote events.
//   5. Your design team card — retailer name, "available now" availability,
//      Send message + Call us buttons, contact info.
//   6. Your next visit card — calendar date block + booking detail + actions.
//
// Section never fetches — all values come from `ctx.project`.

import type {
  CustomerBooking,
  CustomerMessage,
  CustomerProject,
  CustomerSection,
  JourneyStage,
  ProjectJourney,
  ProjectStatus,
  SectionCleanup,
  SectionContext,
} from "../types";

import "./project-overview.css";

// ── Constants ────────────────────────────────────────────────────────────────

/** Heading copy per project status (source-of-truth at customer-live.js:191
 *  uses PROJECT.statusLabel; we derive a sensible default per status). */
const STATUS_HEADLINE: Record<ProjectStatus, string> = {
  lead: "Welcome to your kitchen journey",
  qualified: "Your kitchen journey has begun",
  quoted: "Your kitchen project is in the design phase",
  approved: "Your kitchen is approved — let's build it",
  in_progress: "Your kitchen is being built",
  delivered: "Your kitchen has arrived",
  installed: "Your kitchen is installed",
  complete: "Your kitchen is complete",
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function timeOfDayGreeting(name: string): string {
  const h = new Date().getHours();
  const greet =
    h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
  return name ? `${greet}, ${name}` : greet;
}

function initialsOf(name: string): string {
  const parts = name.split(/[\s&]+/).filter(Boolean);
  const a = (parts[0]?.[0] ?? "?").toUpperCase();
  const b = (parts[1]?.[0] ?? "").toUpperCase();
  return (a + b) || "??";
}

const moneyFmt = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  maximumFractionDigits: 0,
});

function moneyShort(n: number): string {
  if (!Number.isFinite(n)) return "—";
  return moneyFmt.format(n);
}

const apptDateFmt = new Intl.DateTimeFormat("en-GB", {
  weekday: "short",
  day: "numeric",
});

const apptDayFmt = new Intl.DateTimeFormat("en-GB", { weekday: "short" });
const apptMonFmt = new Intl.DateTimeFormat("en-GB", { month: "short" });

const deliveryDateFmt = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
});

const relativeFmt = new Intl.RelativeTimeFormat("en-GB", { numeric: "auto" });

function relativeWhen(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  const diffMs = Date.now() - t;
  const minutes = Math.round(diffMs / 60_000);
  if (Math.abs(minutes) < 60) return relativeFmt.format(-minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return relativeFmt.format(-hours, "hour");
  const days = Math.round(hours / 24);
  if (Math.abs(days) < 7) return relativeFmt.format(-days, "day");
  // Older — show the date.
  return deliveryDateFmt.format(new Date(t));
}

interface NextBooking {
  booking: CustomerBooking;
  date: Date;
}

function pickNextBooking(bookings: CustomerBooking[]): NextBooking | null {
  const now = Date.now();
  let best: NextBooking | null = null;
  for (const b of bookings) {
    if (b.status === "cancelled") continue;
    const t = Date.parse(b.scheduledAt ?? b.preferredDate);
    if (!Number.isFinite(t) || t < now - 24 * 3600 * 1000) continue;
    if (best === null || t < best.date.getTime()) {
      best = { booking: b, date: new Date(t) };
    }
  }
  return best;
}

function unreadInbound(messages: CustomerMessage[]): number {
  return messages.filter((m) => m.direction === "inbound" && !m.read).length;
}

function bookingTitle(b: CustomerBooking): string {
  const map: Record<string, string> = {
    showroom: "Showroom visit",
    home_visit: "Home visit",
    design_consultation: "Design consultation",
    site_survey: "Site survey",
    callback: "Callback",
  };
  return map[b.visitType] ?? humanise(b.visitType);
}

function humanise(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Activity feed assembly ──────────────────────────────────────────────────

interface FeedItem {
  /** Higher = brand-coloured dot (recent), lower = muted dot (older). */
  recent: boolean;
  title: string;
  body: string;
  /** Sort key — most recent first. */
  at: number;
  when: string;
}

function buildFeed(project: CustomerProject): FeedItem[] {
  const items: FeedItem[] = [];

  // Latest 2 inbound messages.
  for (const m of project.messages) {
    if (m.direction !== "inbound") continue;
    const at = Date.parse(m.sentAt);
    if (!Number.isFinite(at)) continue;
    items.push({
      recent: false,
      title: m.subject || "New message from your designer",
      body: m.body.slice(0, 140),
      at,
      when: relativeWhen(m.sentAt),
    });
  }

  // Confirmed bookings.
  for (const b of project.bookings) {
    const at = Date.parse(b.confirmedAt ?? b.requestedAt);
    if (!Number.isFinite(at)) continue;
    items.push({
      recent: false,
      title: `${bookingTitle(b)} ${b.status === "confirmed" ? "confirmed" : "requested"}`,
      body: b.notes || "",
      at,
      when: relativeWhen(b.confirmedAt ?? b.requestedAt),
    });
  }

  // Quote events.
  if (project.quote) {
    if (project.quote.approvedAt) {
      const at = Date.parse(project.quote.approvedAt);
      if (Number.isFinite(at)) {
        items.push({
          recent: false,
          title: "Quote approved",
          body: `${moneyShort(project.quote.totalIncVat)} approved.`,
          at,
          when: relativeWhen(project.quote.approvedAt),
        });
      }
    } else if (project.quote.sentAt) {
      const at = Date.parse(project.quote.sentAt);
      if (Number.isFinite(at)) {
        items.push({
          recent: false,
          title: "Quote received",
          body: `${moneyShort(project.quote.totalIncVat)} — awaiting your review.`,
          at,
          when: relativeWhen(project.quote.sentAt),
        });
      }
    }
  }

  // Sort newest first; mark the two most recent as `recent`.
  items.sort((a, b) => b.at - a.at);
  for (let i = 0; i < items.length && i < 2; i++) items[i]!.recent = true;
  return items.slice(0, 6);
}

// ── Inline SVG icons (custom 24×24, 1.5–2px stroke, currentColor) ───────────

const ICON = {
  message:
    '<path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>',
  calendar:
    '<rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>',
  pound:
    '<line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>',
  truck:
    '<rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>',
  designs:
    '<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>',
  doc:
    '<path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>',
  card:
    '<rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/>',
  clock:
    '<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>',
  users:
    '<path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>',
  phone:
    '<path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>',
  pin:
    '<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>',
};

function svg(path: string, width = 16): string {
  return `<svg viewBox="0 0 24 24" width="${width}" height="${width}" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${path}</svg>`;
}

// ── Journey progress bar (Bug 4) ──────────────────────────────────────────────
// A horizontal stepper at the very top of Home. Stages + state come fully
// derived from the backend (CRM stage, deliveries, installation calendar,
// manual complete); this is pure presentation. Supply-only projects already
// have the Installation stage filtered out server-side.

const CHECK_SVG =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';

function buildJourneyBar(journey: ProjectJourney): HTMLElement {
  const section = document.createElement("section");
  section.className = "po-journey";
  section.setAttribute("aria-label", "Your kitchen journey");

  const steps = journey.stages
    .map((s: JourneyStage, i: number) => {
      const pip =
        s.state === "done"
          ? CHECK_SVG
          : `<span class="po-journey-num">${i + 1}</span>`;
      return `
        <li class="po-journey-step po-journey-step--${s.state}"
            aria-current="${s.state === "current" ? "step" : "false"}">
          <span class="po-journey-pip">${pip}</span>
          <span class="po-journey-label">${esc(s.label)}</span>
        </li>`;
    })
    .join("");

  section.innerHTML = `
    <div class="po-journey-head">Your kitchen journey</div>
    <ol class="po-journey-steps">${steps}</ol>
  `;
  return section;
}

// ── Section ──────────────────────────────────────────────────────────────────

const projectOverview: CustomerSection = {
  id: "project-overview",

  shouldRender(_project: CustomerProject): boolean {
    // Always renders — this IS the home dashboard.
    return true;
  },

  mount(container: HTMLElement, ctx: SectionContext): SectionCleanup {
    const { project } = ctx;
    const { customer, retailer } = project;

    const firstName = (customer.firstName || "").trim();
    const greeting = timeOfDayGreeting(firstName);
    const headline = STATUS_HEADLINE[project.status] ?? "Your kitchen project";
    const projectLine = [
      project.projectName?.trim() || retailer.businessName,
      project.quote?.reference ? `Project #${project.quote.reference}` : "",
    ]
      .filter(Boolean)
      .join(" · ");

    // ── Stat values ─────────────────────────────────────────────────────
    // Prefer the server-computed unread count (unified messaging); fall back to
    // the legacy project.messages tally if the field is absent.
    const unread = project.unreadMessages ?? unreadInbound(project.messages);
    const nextBk = pickNextBooking(project.bookings);
    const latestQuote = project.quote;
    const installEta = project.installDate ? Date.parse(project.installDate) : NaN;
    const firstDelivery = project.deliveries[0];

    const apptValue = nextBk
      ? apptDateFmt.format(nextBk.date)
      : "—";
    const apptMeta = nextBk ? bookingTitle(nextBk.booking) : "No visits scheduled";

    const quoteValue = latestQuote ? moneyShort(latestQuote.totalIncVat) : "—";
    const quoteMeta = latestQuote
      ? latestQuote.status === "approved"
        ? "Approved"
        : "Awaiting your review"
      : "Awaiting first quote";

    let deliveryValue = "—";
    let deliveryMeta = "TBC";
    if (Number.isFinite(installEta)) {
      deliveryValue = deliveryDateFmt.format(new Date(installEta));
      deliveryMeta = "Install date";
    } else if (firstDelivery?.eta) {
      const t = Date.parse(firstDelivery.eta);
      if (Number.isFinite(t)) {
        deliveryValue = deliveryDateFmt.format(new Date(t));
        deliveryMeta = "Delivery ETA";
      }
    }

    // ── Hero ────────────────────────────────────────────────────────────
    const heroCard = document.createElement("section");
    heroCard.className = "po-hero";
    heroCard.setAttribute("aria-labelledby", "po-headline");
    heroCard.innerHTML = `
      <div class="po-hero-greet">${esc(greeting)}</div>
      <h1 id="po-headline" class="po-hero-headline">${esc(headline)}</h1>
      ${projectLine ? `<div class="po-hero-project">${esc(projectLine)}</div>` : ""}
      <div class="po-hero-actions">
        <button class="po-hero-btn po-hero-btn--solid" type="button" data-nav="designs">
          ${svg(ICON.designs, 14)}<span>View latest designs</span>
        </button>
        <button class="po-hero-btn" type="button" data-nav="messages">
          ${svg(ICON.message, 14)}<span>Message your designer</span>
        </button>
        <button class="po-hero-btn" type="button" data-nav="bookings">
          ${svg(ICON.calendar, 14)}<span>Upcoming visit</span>
        </button>
      </div>
    `;
    // Journey progress bar sits above the hero — the customer's first glance
    // lands on "where am I in my project". Hidden when the backend couldn't
    // derive it (no CRM link).
    if (project.journey && project.journey.stages.length) {
      container.appendChild(buildJourneyBar(project.journey));
    }

    container.appendChild(heroCard);

    // ── Stat grid ───────────────────────────────────────────────────────
    const statGrid = document.createElement("div");
    statGrid.className = "po-stat-grid";
    statGrid.innerHTML = `
      <button class="po-stat" type="button" data-nav="messages">
        <span class="po-stat-icon">${svg(ICON.message, 18)}</span>
        <span class="po-stat-label">New messages</span>
        <span class="po-stat-value">${unread}</span>
        <span class="po-stat-meta ${unread > 0 ? "po-stat-meta--brand" : ""}">${unread > 0 ? "From your designer" : "Nothing new"}</span>
      </button>
      <button class="po-stat" type="button" data-nav="bookings">
        <span class="po-stat-icon">${svg(ICON.calendar, 18)}</span>
        <span class="po-stat-label">Next appointment</span>
        <span class="po-stat-value">${esc(apptValue)}</span>
        <span class="po-stat-meta">${esc(apptMeta)}</span>
      </button>
      <button class="po-stat" type="button" data-nav="quote">
        <span class="po-stat-icon">${svg(ICON.pound, 18)}</span>
        <span class="po-stat-label">Latest quote</span>
        <span class="po-stat-value">${esc(quoteValue)}</span>
        <span class="po-stat-meta">${esc(quoteMeta)}</span>
      </button>
      <button class="po-stat" type="button" data-nav="tracking">
        <span class="po-stat-icon">${svg(ICON.truck, 18)}</span>
        <span class="po-stat-label">Estimated delivery</span>
        <span class="po-stat-value">${esc(deliveryValue)}</span>
        <span class="po-stat-meta">${esc(deliveryMeta)}</span>
      </button>
    `;
    container.appendChild(statGrid);

    // ── Quick actions ───────────────────────────────────────────────────
    const qaRow = document.createElement("div");
    qaRow.className = "po-qa-row";
    qaRow.innerHTML = `
      <button class="po-qa-tile" type="button" data-nav="designs">
        <span class="po-qa-ico">${svg(ICON.designs, 20)}</span>
        <span class="po-qa-label">View Designs</span>
      </button>
      <button class="po-qa-tile" type="button" data-nav="quote">
        <span class="po-qa-ico">${svg(ICON.doc, 20)}</span>
        <span class="po-qa-label">Review Quote</span>
      </button>
      <button class="po-qa-tile" type="button" data-nav="payments">
        <span class="po-qa-ico">${svg(ICON.card, 20)}</span>
        <span class="po-qa-label">Make Payment</span>
      </button>
      <button class="po-qa-tile" type="button" data-nav="documents">
        <span class="po-qa-ico">${svg(ICON.doc, 20)}</span>
        <span class="po-qa-label">Documents</span>
      </button>
    `;
    container.appendChild(qaRow);

    // ── Two-column row: Activity feed (left) + Design team / Next visit (right) ─
    const twoCol = document.createElement("div");
    twoCol.className = "po-two-col";

    // Recent activity feed
    const feedItems = buildFeed(project);
    const feedCard = document.createElement("section");
    feedCard.className = "po-card";
    feedCard.innerHTML = `
      <header class="po-card-head">
        <span class="po-card-head-ico">${svg(ICON.clock, 16)}</span>
        <h3 class="po-card-head-title">Recent activity</h3>
        <button type="button" class="po-card-head-link" data-nav="messages">View all</button>
      </header>
      <div class="po-card-body po-card-body--tight">
        <div class="po-feed">
          ${
            feedItems.length === 0
              ? `<div class="po-feed-item"><span class="po-feed-dot po-feed-dot--muted"></span>
                  <div class="po-feed-body">
                    <div class="po-feed-title">No activity yet</div>
                    <div class="po-feed-text">Updates from your designer will appear here.</div>
                  </div>
                </div>`
              : feedItems
                  .map(
                    (it) => `
                <div class="po-feed-item">
                  <span class="po-feed-dot ${it.recent ? "" : "po-feed-dot--muted"}"></span>
                  <div class="po-feed-body">
                    <div class="po-feed-title">${esc(it.title)}</div>
                    ${it.body ? `<div class="po-feed-text">${esc(it.body)}</div>` : ""}
                    ${it.when ? `<div class="po-feed-when">${esc(it.when)}</div>` : ""}
                  </div>
                </div>`,
                  )
                  .join("")
          }
        </div>
      </div>
    `;
    twoCol.appendChild(feedCard);

    // Right column wrapper — design team card + next visit card stacked
    const rightCol = document.createElement("div");
    rightCol.className = "po-right-col";

    // Your design team
    const retailerName = retailer.businessName || "Your retailer";
    const retailerInitials = initialsOf(retailerName);
    // ONLY the platform-allocated Twilio number — never the retailer's
    // personal line ([[feedback-retailer-phone-twilio-only]]). When
    // null we hide the Call us button entirely; do NOT fall back to
    // `retailer.phone`.
    const twilio = retailer.twilioNumber?.trim() ?? "";
    const telHref = twilio.replace(/[^+\d]/g, "");

    const teamCard = document.createElement("section");
    teamCard.className = "po-card";
    teamCard.innerHTML = `
      <header class="po-card-head">
        <span class="po-card-head-ico">${svg(ICON.users, 16)}</span>
        <h3 class="po-card-head-title">Your design team</h3>
      </header>
      <div class="po-card-body">
        <div class="po-team-row">
          <div class="po-team-avatar">${esc(retailerInitials)}</div>
          <div class="po-team-info">
            <div class="po-team-name">${esc(retailerName)}</div>
            <div class="po-team-role">Your kitchen retailer</div>
            <div class="po-team-status">
              <span class="po-team-status-dot"></span>Available now
            </div>
          </div>
        </div>
        <div class="po-team-actions">
          <button class="po-team-btn po-team-btn--solid" type="button" data-nav="messages">
            ${svg(ICON.message, 13)}<span>Send message</span>
          </button>
          ${
            twilio
              ? `<a class="po-team-btn" href="tel:${esc(telHref)}" aria-label="Call ${esc(retailerName)}">
                  ${svg(ICON.phone, 13)}<span>Call us</span>
                </a>`
              : ""
          }
        </div>
      </div>
    `;
    rightCol.appendChild(teamCard);

    // Your next visit — only render if we have one
    if (nextBk) {
      const b = nextBk.booking;
      const d = nextBk.date;
      const day = apptDayFmt.format(d);
      const num = String(d.getDate());
      const mon = apptMonFmt.format(d);
      const timeStr = b.preferredTime
        ? b.preferredTime.replace(/^(\d{2}):(\d{2}).*$/, "$1:$2")
        : "";
      const statusPill =
        b.status === "confirmed"
          ? "cust-pill-status brand"
          : b.status === "completed"
            ? "cust-pill-status green"
            : "cust-pill-status blue";
      const statusLabel = b.status === "confirmed"
        ? "Confirmed"
        : b.status === "completed"
          ? "Completed"
          : b.status === "pending"
            ? "Pending"
            : humanise(b.status);

      const visitCard = document.createElement("section");
      visitCard.className = "po-card";
      visitCard.innerHTML = `
        <header class="po-card-head">
          <span class="po-card-head-ico">${svg(ICON.calendar, 16)}</span>
          <h3 class="po-card-head-title">Your next visit</h3>
          <span class="${statusPill}">${esc(statusLabel)}</span>
        </header>
        <div class="po-card-body">
          <div class="po-visit-row">
            <div class="po-visit-date">
              <div class="po-visit-day">${esc(day)}</div>
              <div class="po-visit-num">${esc(num)}</div>
              <div class="po-visit-mon">${esc(mon)}</div>
            </div>
            <div class="po-visit-info">
              <div class="po-visit-title">${esc(bookingTitle(b))}</div>
              ${
                b.notes
                  ? `<div class="po-visit-notes">${esc(b.notes)}</div>`
                  : `<div class="po-visit-notes">Your retailer will be in touch to confirm the details.</div>`
              }
              <div class="po-visit-meta">
                ${
                  timeStr
                    ? `<span>${svg(ICON.clock, 13)}${esc(timeStr)}</span>`
                    : ""
                }
                <span>${svg(ICON.pin, 13)}${esc(
                  b.visitType === "showroom"
                    ? `${retailerName} showroom`
                    : "Your address",
                )}</span>
              </div>
            </div>
          </div>
        </div>
      `;
      rightCol.appendChild(visitCard);
    }

    twoCol.appendChild(rightCol);
    container.appendChild(twoCol);

    // ── Wiring ──────────────────────────────────────────────────────────
    const onClick = (e: MouseEvent): void => {
      const target = (e.target as HTMLElement | null)?.closest<HTMLElement>(
        "[data-nav]",
      );
      if (!target) return;
      const page = target.dataset["nav"];
      if (!page) return;
      e.preventDefault();
      window.dispatchEvent(
        new CustomEvent("cust-nav", { detail: { page } }),
      );
    };
    container.addEventListener("click", onClick);

    // When the Messages tab marks conversations read, clear the "New messages"
    // stat in place so its count + brand dot disappear immediately, rather
    // than lingering until the next 15s project refresh.
    const onMessagesRead = (): void => {
      const stat = statGrid.querySelector<HTMLElement>('.po-stat[data-nav="messages"]');
      if (!stat) return;
      const valueEl = stat.querySelector<HTMLElement>(".po-stat-value");
      const metaEl = stat.querySelector<HTMLElement>(".po-stat-meta");
      if (valueEl) valueEl.textContent = "0";
      if (metaEl) {
        metaEl.textContent = "Nothing new";
        metaEl.classList.remove("po-stat-meta--brand");
      }
    };
    window.addEventListener("tkos:cust-messages-read", onMessagesRead);

    return () => {
      container.removeEventListener("click", onClick);
      window.removeEventListener("tkos:cust-messages-read", onMessagesRead);
      container.replaceChildren();
    };
  },
};

export default projectOverview;
